import * as THREE from 'three';
import type {
  ActiveSelectionTool,
  SelectionDragSession,
  SelectionToolContext,
} from './activeSelectionTool';

type Axis = 'x' | 'y' | 'z';

type RotateDragState = {
  axis: THREE.Vector3;
  plane: THREE.Plane;
  startAngle: number;
  basisU: THREE.Vector3;
  basisV: THREE.Vector3;
  center: THREE.Vector3;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
    offsetFromCenter: THREE.Vector3;
  }>;
};

const AXIS_VECTORS: Record<Axis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

const ROTATION_SNAP_STEP = Math.PI / 12;

export class RotateSelectionTool implements ActiveSelectionTool {
  readonly id = 'rotate' as const;

  private readonly center = new THREE.Vector3();
  private readonly size = new THREE.Vector3();
  private readonly gizmoRoot = new THREE.Group();
  private readonly pickTargets: THREE.Object3D[] = [];
  private visible = false;

  constructor() {
    this.gizmoRoot.visible = false;
    this.createRotateGizmo();
  }

  render(parent: THREE.Object3D): void {
    parent.add(this.gizmoRoot);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.gizmoRoot.visible = visible;
  }

  getPickTargets(): THREE.Object3D[] {
    if (!this.visible) {
      return [];
    }

    return this.pickTargets;
  }

  updateTransform(center: THREE.Vector3, size: THREE.Vector3): void {
    this.center.copy(center);
    this.size.copy(size);

    const radius = Math.max(size.length() * 0.35, 1.25);
    this.gizmoRoot.position.copy(this.center);
    this.gizmoRoot.scale.setScalar(radius);
  }

  startDrag(context: SelectionToolContext): SelectionDragSession | null {
    const axisName = context.intersection.object.userData.axis as
      | Axis
      | undefined;
    if (!axisName) {
      return null;
    }

    const axis = AXIS_VECTORS[axisName].clone();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      axis,
      this.center,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const basisU = this.getPerpendicularAxis(axis);
    const basisV = new THREE.Vector3().crossVectors(axis, basisU).normalize();

    const startDirection = startPoint.clone().sub(this.center);
    if (startDirection.lengthSq() < 1e-8) {
      return null;
    }

    const startAngle = Math.atan2(
      startDirection.dot(basisV),
      startDirection.dot(basisU),
    );

    const dragState: RotateDragState = {
      axis,
      plane,
      startAngle,
      basisU,
      basisV,
      center: this.center.clone(),
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
        quaternion: object.quaternion.clone(),
        offsetFromCenter: object.position.clone().sub(this.center),
      })),
    };

    return this.createDragSession(dragState);
  }

  dispose(): void {
    for (const child of this.gizmoRoot.children) {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
  }

  private createDragSession(dragState: RotateDragState): SelectionDragSession {
    return {
      updateDrag: (ray, snapToHalfStep) => {
        const dragPoint = ray.intersectPlane(
          dragState.plane,
          new THREE.Vector3(),
        );
        if (!dragPoint) {
          return;
        }

        const direction = dragPoint.clone().sub(dragState.center);
        if (direction.lengthSq() < 1e-8) {
          return;
        }

        const dragAngle = Math.atan2(
          direction.dot(dragState.basisV),
          direction.dot(dragState.basisU),
        );

        let deltaAngle = dragAngle - dragState.startAngle;
        deltaAngle = Math.atan2(Math.sin(deltaAngle), Math.cos(deltaAngle));

        if (snapToHalfStep) {
          deltaAngle =
            Math.round(deltaAngle / ROTATION_SNAP_STEP) * ROTATION_SNAP_STEP;
        }

        const deltaQuaternion = new THREE.Quaternion().setFromAxisAngle(
          dragState.axis,
          deltaAngle,
        );

        for (const state of dragState.initialStates) {
          const newOffset = state.offsetFromCenter
            .clone()
            .applyQuaternion(deltaQuaternion);
          const newPosition = dragState.center.clone().add(newOffset);
          const newQuaternion = deltaQuaternion
            .clone()
            .multiply(state.quaternion);

          state.object.setPosition(newPosition.x, newPosition.y, newPosition.z);
          state.object.setQuaternion(
            newQuaternion.x,
            newQuaternion.y,
            newQuaternion.z,
            newQuaternion.w,
          );
        }
      },
    };
  }

  private getPerpendicularAxis(axis: THREE.Vector3): THREE.Vector3 {
    const up =
      Math.abs(axis.y) < 0.9
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);
    return new THREE.Vector3().crossVectors(axis, up).normalize();
  }

  private createRotateGizmo() {
    const ringConfigs: Array<{
      axis: Axis;
      color: number;
      rotation: THREE.Euler;
    }> = [
      {
        axis: 'x',
        color: 0xff7a7a,
        rotation: new THREE.Euler(0, Math.PI / 2, 0),
      },
      {
        axis: 'y',
        color: 0x72f495,
        rotation: new THREE.Euler(Math.PI / 2, 0, 0),
      },
      {
        axis: 'z',
        color: 0x7ab8ff,
        rotation: new THREE.Euler(0, 0, 0),
      },
    ];

    for (const config of ringConfigs) {
      const visualRing = new THREE.Mesh(
        new THREE.TorusGeometry(1, 0.018, 16, 96),
        new THREE.MeshBasicMaterial({
          color: config.color,
          depthTest: false,
          transparent: true,
          opacity: 0.92,
        }),
      );
      visualRing.rotation.copy(config.rotation);
      visualRing.renderOrder = 1000;

      const pickRing = new THREE.Mesh(
        new THREE.TorusGeometry(1, 0.14, 12, 72),
        new THREE.MeshBasicMaterial({
          visible: false,
          depthTest: false,
        }),
      );
      pickRing.rotation.copy(config.rotation);
      pickRing.userData.axis = config.axis;
      pickRing.userData.dragPriority = 1;

      this.gizmoRoot.add(visualRing);
      this.gizmoRoot.add(pickRing);
      this.pickTargets.push(pickRing);
    }
  }
}
